import { FC, useCallback, useMemo } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { Offer } from '@/services/PifService'
import { getStripe } from '@/services/StripeService/StripePaymentService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { ExpressPaymentSkeleton } from '@/views/ExpressCheckoutView/components/ExpressPaymentMethods/ExpressPaymentSkeleton'
import { ExpressStripeForm } from '@/views/ExpressCheckoutView/components/ExpressPaymentMethods/ExpressStripeForm'
import { addGuildUpsellToOffer } from '@/views/ExpressCheckoutView/utils'
import { useClientSecret } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useClientSecret'
import {
  OrderCompleteArgs,
  PayItForwardFormProvider,
} from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm/PayItForwardFormContext'

interface Props {
  selectedOffer: Offer
  projectSlug: string
  isGuildMembershipAdded?: boolean
  theatricalRegion?: string
}

export const ExpressPaymentMethods: FC<Props> = ({
  projectSlug,
  selectedOffer,
  isGuildMembershipAdded,
  theatricalRegion,
}) => {
  const { track } = useSafeAnalytics()
  const { user } = useUser()
  const { push } = useSafeRouter()
  const { locale } = useLocale()

  const offerWithGuildAdded = useMemo(() => addGuildUpsellToOffer(selectedOffer), [selectedOffer])
  const { clientSecret, loading: clientSecretLoading } = useClientSecret({
    offer: isGuildMembershipAdded ? offerWithGuildAdded : selectedOffer,
  })

  const onComplete = useCallback(
    (args?: Omit<OrderCompleteArgs, 'paymentMethod'>) => {
      const params = {
        token: args?.token,
        email: args?.email,
        guildMembershipIncluded: isGuildMembershipAdded ? '1' : '0',
      }

      const queryParams = Object.entries(params)
        .filter(([_, value]) => !!value)
        .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
        .join('&')

      track('Express Order Completed')
      const path = theatricalRegion
        ? `/express-checkout/${projectSlug}/${theatricalRegion}/checkout/${selectedOffer?.id}`
        : `/express-checkout/${projectSlug}/checkout/${selectedOffer?.id}`
      push(`${path}?${queryParams}`)
    },
    [isGuildMembershipAdded, track, theatricalRegion, projectSlug, selectedOffer?.id, push],
  )

  const isPaymentInfoReady = selectedOffer && clientSecret && !clientSecretLoading

  if (!isPaymentInfoReady) return <ExpressPaymentSkeleton />

  return (
    <Elements
      key={clientSecret}
      stripe={getStripe(locale)}
      options={{
        clientSecret,
        appearance: { theme: 'stripe' },
        loader: 'auto',
      }}
    >
      <PayItForwardFormProvider
        onComplete={onComplete}
        offer={selectedOffer}
        projectSlug={projectSlug}
        user={user}
        guildMembershipIncluded={isGuildMembershipAdded}
      >
        <ExpressStripeForm
          offer={selectedOffer}
          clientSecret={clientSecret}
          isGuest={!user}
          projectSlug={projectSlug}
          guildMembershipIncluded={isGuildMembershipAdded}
        />
      </PayItForwardFormProvider>
    </Elements>
  )
}
